// A transfer (UI design §3.5): open it to receive (ticked lines; unticked become "not received") or cancel it. The
// transfers list is gone (CR_20260915_Request_Qty R0): a place's transfers are in its Transfers tab, and this page
// links back to the place it belongs to for the viewer (destination first, else origin).
import type { Transfer } from '@fleetspoke/core/types';
import {
  Alert,
  AlertTitle,
  Button,
  Checkbox,
  Chip,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams } from 'react-router';
import { useSession } from '../auth/session';
import { usePlaceNames } from '../components/NewTransferDialog';
import { Empty, Loading, Page } from '../components/Page';
import { useSiteBoard, useSites } from '../data/queries';
import { useLiveInvalidate } from '../data/realtime';
import {
  OPEN_TRANSFER_STATUSES as OPEN,
  TRANSFER_STATUS_COLOR as STATUS_COLOR,
  useAllTransfers,
  useCancelTransfer,
  useReceiveTransfer,
  workflowKeys,
} from '../data/workflow';
import { placePath, transferHome } from '../lib/places';
import { dateTime } from '../lib/time';

export function TransferDetailPage() {
  const { t, i18n } = useTranslation();
  const { transferId = '' } = useParams();
  const { me } = useSession();
  const transfers = useAllTransfers();
  const sites = useSites();
  const receive = useReceiveTransfer();
  const cancel = useCancelTransfer();
  const siteName = usePlaceNames();
  const tr = transfers.data?.find((x) => x.id === transferId) as
    | (Transfer & {
        note: string | null;
        request_id?: string | null;
        asset_requests?: { ref: string } | null;
      })
    | undefined;
  const board = useSiteBoard(tr?.to_site_id ?? '');
  const pending = useMemo(
    () => (tr?.transfer_lines ?? []).filter((l) => l.status === 'pending'),
    [tr],
  );
  const [unticked, setUnticked] = useState<Set<string>>(new Set());
  const [note, setNote] = useState('');
  useLiveInvalidate(
    `transfer-${transferId}`,
    ['transfers', 'transfer_lines'],
    [workflowKeys.transfers],
  );

  if (transfers.isLoading) return <Loading />;
  if (!tr)
    return (
      <Page title={t('transfers.title')}>
        <Empty>{t('m.receive.unavailable')}</Empty>
      </Page>
    );
  const home = transferHome(tr, sites.data ?? []);
  const onSite = new Set(
    (board.data ?? [])
      .filter((a) => a.presence_site_id === tr.to_site_id && a.presence_state === 'on_site')
      .map((a) => a.asset_id),
  );
  const open = OPEN.includes(tr.status);
  const ticked = pending.filter((l) => !unticked.has(l.asset_id));

  return (
    <Page
      title={`${tr.ref} · ${siteName(tr.from_site_id, 'from')} → ${siteName(tr.to_site_id, 'to')}`}
      back={
        home ? (
          <Link
            component={RouterLink}
            to={placePath(home, 'movements')}
            variant="body2"
            data-testid="transfer-back"
          >
            ← {home.name}
          </Link>
        ) : undefined
      }
    >
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Chip color={STATUS_COLOR[tr.status]} label={t(`m.transfer.status.${tr.status}`)} />
        {tr.created_by_system ? <Chip label={t('transfers.automatic')} /> : null}
        {/* made from a request (CR_20260915_Request_Qty): the paper trail back to it */}
        {tr.request_id && tr.asset_requests ? (
          <Chip
            component={RouterLink}
            to={`/requests/${tr.request_id}`}
            clickable
            variant="outlined"
            label={t('transfers.from_request', { ref: tr.asset_requests.ref })}
            data-testid="transfer-request"
          />
        ) : null}
      </Stack>
      {/* CR_20260921 L6a: sent although a certificate blocked it — the manager's reason, for everyone to see */}
      {tr.cert_override ? (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid="transfer-override">
          <AlertTitle>{t('transfer.override_title')}</AlertTitle>
          {tr.cert_override.reason}
          <br />
          {t('transfer.override_by', {
            by: tr.cert_override.by_name ?? '—',
            when: dateTime(
              tr.cert_override.at,
              i18n.language,
              me?.company?.timezone ?? 'Asia/Kuala_Lumpur',
            ),
          })}
          {' · '}
          {tr.cert_override.assets.map((a) => `${a.code} (${a.kind})`).join(', ')}
        </Alert>
      ) : null}
      {tr.note ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {tr.note}
        </Alert>
      ) : null}
      <Table size="small" sx={{ mb: 2 }}>
        <TableHead>
          <TableRow>
            {open && pending.length ? <TableCell padding="checkbox" /> : null}
            <TableCell>{t('assets.code')}</TableCell>
            <TableCell>{t('assets.description')}</TableCell>
            <TableCell>{t('transfers.line_status')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tr.transfer_lines.map((l) => (
            <TableRow key={l.id}>
              {open && pending.length ? (
                <TableCell padding="checkbox">
                  {l.status === 'pending' ? (
                    <Checkbox
                      checked={!unticked.has(l.asset_id)}
                      onChange={(e) =>
                        setUnticked((u) => {
                          const next = new Set(u);
                          if (e.target.checked) next.delete(l.asset_id);
                          else next.add(l.asset_id);
                          return next;
                        })
                      }
                      inputProps={{ 'aria-label': l.assets?.asset_code ?? '' }}
                    />
                  ) : null}
                </TableCell>
              ) : null}
              <TableCell>{l.assets?.asset_code}</TableCell>
              <TableCell>{l.assets?.description ?? ''}</TableCell>
              <TableCell>
                {t(`transfers.line_${l.status}`)}
                {l.status === 'pending' && onSite.has(l.asset_id)
                  ? ` · ${t('transfers.on_site_now')}`
                  : ''}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {open ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
          {pending.length ? (
            <Button
              variant="contained"
              disabled={receive.isPending}
              onClick={() =>
                receive.mutate({
                  transferId: tr.id,
                  assetIds: ticked.length === pending.length ? null : ticked.map((l) => l.asset_id),
                })
              }
            >
              {t('m.receive.confirm', { count: ticked.length, total: pending.length })}
            </Button>
          ) : null}
          <TextField
            size="small"
            label={`${t('transfers.cancel_reason')} (${t('common.optional')})`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <Button
            color="error"
            disabled={cancel.isPending}
            onClick={() => cancel.mutate({ transferId: tr.id, note })}
          >
            {t('transfers.cancel')}
          </Button>
        </Stack>
      ) : null}
      {receive.error || cancel.error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {((receive.error ?? cancel.error) as { code?: string }).code === '42501'
            ? t('common.not_allowed')
            : ((receive.error ?? cancel.error) as Error).message}
        </Alert>
      ) : null}
      {me && !open && tr.status === 'disputed' ? (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {t('transfers.disputed_help')}
        </Alert>
      ) : null}
    </Page>
  );
}
